import { DotLegalTableState } from "@dotlegal/dotlegal-ui-components";
import { FilterOperatorUi } from "@common/api/filterTypes";
import { FieldGroupingType, FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types";
import { DateFieldValue } from "@common/components/filterPopover/subMenues/DateFieldSubMenu";
import { TableFields } from "@common/components/tableDisplayFields/TableFields.types";
import { GetListData } from "@common/components/filterPopover/subMenues/SubMenuDataHelper";
import { useSavedViewQuery } from "@common/components/savedViews/SavedViewsMenu.hooks";
import { SavedView } from "@common/components/savedViews/SavedViewsMenu.types";
import { usePageView } from "./PageView.hooks";
import { FilterPage, SessionStorageSavedView } from "./PageView.types";

// Mirrors Backend/Core/Filtering/Audits/AuditFilterSnapshot.cs, the shape every client-filtered table saves.
export interface ClientFilteredSavedFilter {
    selectFilters: Record<string, Array<string>>;
    filterOperators: Record<string, FilterOperatorUi>;
    sortByColumn?: string;
    sortDirection?: "asc" | "desc";
}

// Must deep-equal the backend strategy's GetDefaultFilterData after a JSON round-trip, or the system default view
// would be flagged as changed before the user touches anything.
const defaultFilter: ClientFilteredSavedFilter = {
    selectFilters: {},
    filterOperators: {},
};

// A saved view stores select filters only, which every table using this hook filters by.
const noDateFilters: Record<string, DateFieldValue | null> = {};

export interface ClientFilteredPageViewOptions<TColumn extends string> {
    page: FilterPage;
    // The columns this user may see right now. A saved view persists raw column ids, so one written before a column
    // was removed or while the customer had a feature, would otherwise render a header with no cell.
    availableColumns: Array<TColumn>;
    defaultOrderBy: string;
    defaultOrder: "asc" | "desc";
}

export function toTableFields<TColumn extends string>(
    columns: Array<TColumn>,
    columnLabels: Record<TColumn, string>,
    grouping: FieldGroupingType
): Array<TableFields> {
    return columns.map((column) => ({ id: column, name: columnLabels[column], grouping }));
}

export function getRenderedColumns<TColumn extends string>(columns: Array<TColumn>, availableColumns: Array<TColumn>): Array<TColumn> {
    return columns.filter((column) => availableColumns.includes(column));
}

// The column picker drops columns that are not available while the saved view keeps them, so comparing the raw
// lists would read an untouched view as changed. Only what is actually rendered counts.
export function hasRenderedColumnsChanged<TColumn extends string>(
    columns: Array<TColumn>,
    savedColumns: Array<TColumn>,
    availableColumns: Array<TColumn>
): boolean {
    return JSON.stringify(getRenderedColumns(columns, availableColumns)) !== JSON.stringify(getRenderedColumns(savedColumns, availableColumns));
}

// Cleared filters are dropped (no empty arrays, no orphaned operators) so the persisted shape deep-equals a saved
// view's JSON round-trip, otherwise change detection would flag phantom differences.
export function toSavedFilter(previous: ClientFilteredSavedFilter, options: Array<FilterOption>): ClientFilteredSavedFilter {
    const selectFilters: Record<string, Array<string>> = {};
    const filterOperators: Record<string, FilterOperatorUi> = {};

    options
        .filter((option) => option.type === FilterOptionType.SelectField)
        .forEach((option) => {
            const values = GetListData(option.value);
            if (values.length > 0) {
                selectFilters[option.id] = values;
                if (option.filterOperator !== undefined) {
                    filterOperators[option.id] = option.filterOperator;
                }
            }
        });

    return { ...previous, selectFilters, filterOperators };
}

export function useClientFilteredPageView<TColumn extends string>(options: ClientFilteredPageViewOptions<TColumn>) {
    const savedViewQuery = useSavedViewQuery(options.page);
    const {
        currentViewId,
        tableSessionStateKey,
        getFilterData,
        getDisplayData,
        setFilterData,
        setDisplayData,
        clearFilterData,
        hasFilterChanged,
        hasDisplayChanged: hasStoredDisplayChanged,
        isSavedView,
        savedView,
        setView,
        pinnedFilterIds,
        setPinnedFilterIds,
    } = usePageView<ClientFilteredSavedFilter, Array<TColumn>>(options.page, savedViewQuery.data);

    const filterData = getFilterData<ClientFilteredSavedFilter>() ?? defaultFilter;
    // The backend system-default view supplies the default columns once saved views resolve; until then the page
    // shows its skeleton, so an empty fallback never renders.
    const visibleColumns = getDisplayData<Array<TColumn>>() ?? [];
    const renderedColumns = getRenderedColumns(visibleColumns, options.availableColumns);
    const hasDisplayChanged =
        hasStoredDisplayChanged && hasRenderedColumnsChanged(visibleColumns, (savedView?.displayData ?? []) as Array<TColumn>, options.availableColumns);

    // Shared by the table and the export so both order rows identically.
    const sortOrderBy = filterData.sortByColumn ?? options.defaultOrderBy;
    const sortOrder = filterData.sortDirection ?? options.defaultOrder;

    function onFilterChange(updatedOptions: Array<FilterOption>) {
        setFilterData(toSavedFilter(filterData, updatedOptions));
    }

    // DotLegalTable also emits onChange on page changes, carrying the unchanged sort, so only a real sort change is
    // written to the view.
    function onTableStateChange<TRow>(state: DotLegalTableState<TRow>) {
        if (!state.property) {
            return;
        }

        const orderBy = String(state.property);
        if (orderBy !== sortOrderBy || state.order !== sortOrder) {
            setFilterData({ ...filterData, sortByColumn: orderBy, sortDirection: state.order });
        }
    }

    function onColumnsSave(columns: Array<string>) {
        setDisplayData(columns.filter((column): column is TColumn => options.availableColumns.includes(column as TColumn)));
    }

    function applyView(viewId: string | undefined, filter: ClientFilteredSavedFilter, display: Array<TColumn>) {
        const updatedView: SessionStorageSavedView = {
            page: options.page,
            viewId,
            filterData: filter,
            displayData: display,
        };
        setView(updatedView);
    }

    function onViewSaved(viewId: string) {
        applyView(viewId, filterData, visibleColumns);
    }

    function onViewDeleted(deletedViewId: string) {
        if (deletedViewId !== currentViewId) {
            return;
        }

        const fallbackView = savedViewQuery.data?.find((x: SavedView) => x.isCustomerDefault || x.isSystemDefault);
        applyView(fallbackView?.id === deletedViewId ? undefined : fallbackView?.id, filterData, visibleColumns);
    }

    function onChangeView(view: SavedView) {
        if (view.id !== currentViewId) {
            applyView(view.id, view.filterData, view.displayData);
        }
    }

    // The filter members are named as useFilterState names them, so a table can take either one as its filter state.
    return {
        page: options.page,
        savedViewsAreLoading: savedViewQuery.isLoading,
        filterData,
        selectFilters: filterData.selectFilters,
        dateFilters: noDateFilters,
        filterOperators: filterData.filterOperators,
        onFilterChange,
        clearFilterData,
        hasFilterChanged,
        pinnedFilterIds,
        onPinnedFilterIdsChange: setPinnedFilterIds,
        visibleColumns,
        renderedColumns,
        onColumnsSave,
        hasDisplayChanged,
        savedView,
        currentViewId,
        isSavedView,
        tableSessionStateKey,
        sortOrderBy,
        sortOrder,
        onTableStateChange,
        onViewSaved,
        onViewDeleted,
        onChangeView,
    };
}

export type ClientFilteredPageView<TColumn extends string> = ReturnType<typeof useClientFilteredPageView<TColumn>>;
